import React, { memo } from 'react';
import Type from 'prop-types';
import {
  Box,
  Card,
  CardContent,
  Divider,
  Grid,
  Typography,
} from "@mui/material";
import { useTranslation } from 'hooks';
import { FormattedNumber, FormattedDate } from 'components';
import { fullDateFormat } from 'constants/dateFormats';
import styles from './CascadeItem.module.css';

const CascadeItem = memo((props) => {
  const { data, onClick } = props;
  const { t } = useTranslation();

  const handleClick = () => {
    onClick(data.entityId);
  };

  const renderInfo = () => {
    return (
      <Box pt={1} pb={2}>
        <Grid container justifyContent="space-between">
          <Typography variant="body1" color="textSecondary" component="span">
            {`${t('responsibleGroup')}:`}
          </Typography>
          <Typography variant="body1" component="span">
            {data.responsibleGroup.name}
          </Typography>
        </Grid>
        {data.promisedDate && (
          <Grid container justifyContent="space-between">
            <Typography variant="body1" color="textSecondary" component="span">
              {`${t('promisedDate')}:`}
            </Typography>
            <FormattedDate
              value={data.promisedDate}
              token={fullDateFormat}
              color="textPrimary"
              variant="body1"
            />
          </Grid>
        )}
        <Grid container justifyContent="space-between">
          <Typography variant="body1" color="textSecondary" component="span">
            {`${t('pdn')}:`}
          </Typography>
          <FormattedNumber
            component="span"
            value={data.overdueDays}
            variant="body1"
            color="textPrimary"
          />
        </Grid>
        <Grid container justifyContent="space-between">
          <Typography variant="body1" color="textSecondary" component="span">
            {`${t('sumCreditLimit')}: `}
          </Typography>
          <FormattedNumber
            component="span"
            value={data.creditLimit}
            variant="body1"
            color="textPrimary"
            options={{
              currency: 'RUB',
              style: 'currency',
              minimumFractionDigits: 0,
            }}
          />
        </Grid>
        <Grid container justifyContent="space-between">
          <Typography variant="body1" color="textSecondary" component="span">
            {`${t('weeklyPayments')}: `}
          </Typography>
          <FormattedNumber
            component="span"
            value={data.payments}
            variant="body1"
            color="textPrimary"
            options={{
              currency: 'RUB',
              style: 'currency',
              minimumFractionDigits: 0,
            }}
          />
        </Grid>
      </Box>
    );
  };

  return (
    <Card onClick={handleClick} square data-testid="cascadeItemWrapper">
      <CardContent className={styles.content}>
        <Typography
          variant="subtitle1"
          className={styles.title}
          sx={{ fontWeight: 500 }}
        >
          {data.debitor.name}
        </Typography>

        {renderInfo()}

        <Divider />
        <Box pt={1}>
          <Grid container justifyContent="space-between">
            <Grid item xs={6}>
              <Typography
                variant="body2"
                color="primary"
                component="div"
                classes={{ root: styles.subtitle }}
              >
                {t('pdz')}
              </Typography>
              <FormattedNumber
                component="div"
                value={data.overdueDebt}
                variant="body1"
                color="textPrimary"
                options={{
                  currency: 'RUB',
                  style: 'currency',
                  minimumFractionDigits: 0,
                }}
              />
            </Grid>

            <Grid item xs={6}>
              <Typography
                variant="body2"
                color="textSecondary"
                component="div"
                textAlign="right"
              >
                {t('dz')}
              </Typography>
              <FormattedNumber
                component="div"
                value={data.totalDebt}
                variant="body1"
                color="textPrimary"
                textAlign="right"
                options={{
                  currency: 'RUB',
                  style: 'currency',
                  minimumFractionDigits: 0,
                }}
              />
            </Grid>
          </Grid>
        </Box>
      </CardContent>
    </Card>
  );
});

CascadeItem.propTypes = {
  data: Type.shape({
    entityId: Type.string.isRequired,
    debitor: Type.shape({
      id: Type.string.isRequired,
      name: Type.string.isRequired,
    }).isRequired,
    responsibleGroup: Type.shape({
      id: Type.string.isRequired,
      name: Type.string.isRequired,
    }).isRequired,
    promisedDate: Type.string,
    promisedPayment: Type.number.isRequired,
    overdueDays: Type.number.isRequired,
    overdueDebt: Type.number.isRequired,
    totalDebt: Type.number.isRequired,
    creditLimit: Type.number.isRequired,
    payments: Type.number.isRequired,
  }).isRequired,
  onClick: Type.func.isRequired,
};

export default CascadeItem;
